import { computed } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import { usePlayMixStore } from '~/stores/usePlayMixStore';

export default function useMultiplePlayMixServices(appContext) {
  const hubbleClient = appContext.$hubble;
  const playMixStore = usePlayMixStore()

  const {
    mixVASLimits,
    selectedPlayServices,
    playPromotionalContent,
    playMixMarketOffers,
    playPartners,
    vasLimit,
    selectedPartners,
    selectedServices,
    playMixDescriptions
  } = storeToRefs(playMixStore)

  // Fetch mixTemporaryCFTexts data from the JSON file
  const preLoadPlayMixDescriptions = async () => {
    try {
      // Fetch mixTemporaryCFTexts data from the JSON file
      const response = await fetch('mixTemporaryCFTexts.json');
      playMixDescriptions.value = await response.json();
    } catch (error) {
      console.error('Error fetching mixTemporaryCFTexts.json:', error);
    }
  }

  // Lazy initialization to be called in the component that calls getMixSelectionDescription(..)
  const initialize = async () => {
    await preLoadPlayMixDescriptions();
  };

  // Return a computed property that reacts to changes in playMixDescriptions
  const getMixSelectionDescription = (vasId) => {
    return playMixDescriptions.value?.[vasId]?.mixSelectionDescription || [];
  };

  function parsePlayPromotionalPayload (data) {
    if (Array.isArray(data)) {
      return data
    }
    const nested = data?.responseObject ?? data?.data ?? data
    return Array.isArray(nested) ? nested : []
  }

  const getPlayPromotionalContent = async () => {
    const res = await hubbleClient.playMix.getPlayPromotionalContent()
    const list = parsePlayPromotionalPayload(res?.data)
    playPromotionalContent.value = list
  }
  const getMixPartners = async(bundleId) => {
    const res = await hubbleClient.playMix.getMixPartners(bundleId);
    // sort partners by priority
    res.data.partners.sort((a, b) => a.priority - b.priority);
    // sort versions  and plans inside partners by default value true to be before false
    res.data.partners.forEach(partner => {
      partner.plans.sort((a, b) => b.isDefaultPlan - a.isDefaultPlan);
      partner.plans.forEach(plan => {
        plan.versions.sort((a, b) => b.isDefaultVersion - a.isDefaultVersion);
      });
    });
    playPartners.value = res.data.partners
    mixVASLimits.value = res.data.mixVASLimits
  }
  const getMixMarketOffers = async() => {
    const res = await hubbleClient.playMix.getMixMarketOffers();
    playMixMarketOffers.value = res.data.marketOffers
  }

  const handlePartnerChange = (item) => {
    // Check if partner is present in selectedPartners
    if (selectedPartners.value.includes(item.name)) {
      // If present then remove the partner from the list of selectedPartners
      selectedPartners.value = selectedPartners.value.filter(val => val !== item.name);
    } else if (selectedPartners.value.length < vasLimit.value) {
      // If present then add the partner from the list of selectedPartners
      selectedPartners.value = [...selectedPartners.value, item.name];
    } else {
      return false;
    }
    if (item.plans.length > 1) {
      const defaultPlan = item.plans.find(plan => plan.isDefaultPlan)
      handleProductOfferingChange(item, defaultPlan)
    } else {
      handleProductOfferingChange(item)
    }
  }

  const handleProductOfferingChange = (item, plan = null) => {
    let trueValue = item.plans.at(0).versions.find(version => !version.isDefaultVersion);
    let falseValue = item.plans.at(0).versions.find(version => version.isDefaultVersion);
    if (plan !== null) {
      // remove the previous plan and add the new plan ( default service of each )
      trueValue = item.plans.find(val => val.type !== plan.type).versions.find(version => version.isDefaultVersion);
      falseValue = plan.versions.find(version => version.isDefaultVersion);
    }
    const selectedServiceNames = selectedServices.value.map(object => object.name)
    if (selectedPartners.value.includes(item.name)) {
      // Check if value is present in selectedServices if so remove it and add the other value
      if (selectedServiceNames.includes(falseValue?.name)) {
        // If switch is turned off, remove trueValue and add falseValue
        selectedServices.value = selectedServices.value.filter(val => val.name !== falseValue.name);
        selectedServices.value.push(trueValue);
      } else {
        // If switch is turned on, remove falseValue and add trueValue
        selectedServices.value = selectedServices.value.filter(val => val.name !== trueValue?.name);
        selectedServices.value.push(falseValue);
      }
    } else {
      selectedServices.value = selectedServices.value.filter(val => val.name !== trueValue?.name && val.name !== falseValue?.name)
    }
  }
  const numberOfSelectedAdServices = computed(() => {
    const servicesWithAds = selectedServices.value.filter(service => service.isWithAds)
    return servicesWithAds ? servicesWithAds.length : 0
  })
  const selectedPlayPartners = computed(() => {
    return playPartners?.value?.filter(partner => selectedPartners.value.includes(partner?.name))
  })
  const getImage = (vasId) => {
    const vasIdLower = String(vasId ?? '').toLowerCase()
    return playPromotionalContent.value.find(partner =>
      Array.isArray(partner?.vasIds) && partner.vasIds.some(id => String(id ?? '').toLowerCase() === vasIdLower)
    )?.iconImageEntry?.image?.imageUrl
  }

  const selectedMarketOffering = computed(() => {
    return playMixMarketOffers.value.find(mo => mo.noOfVAS === vasLimit.value && mo.noOfAds === numberOfSelectedAdServices.value)
  })
  const getProductConfigurationProductCatalog = async() => {
    const res = await hubbleClient.playMix.getProductConfigurationProductCatalog();
    return res.data
  }

  return {
    getProductConfigurationProductCatalog,
    getMixSelectionDescription,
    mixVASLimits,
    selectedPlayServices,
    numberOfSelectedAdServices,
    selectedMarketOffering,
    getImage,
    getPlayPromotionalContent,
    playPromotionalContent,
    playMixMarketOffers,
    getMixMarketOffers,
    getMixPartners,
    vasLimit,
    selectedPlayPartners,
    selectedServices,
    selectedPartners,
    handleProductOfferingChange,
    handlePartnerChange,
    playPartners,
    initialize
  }
}
